import { ButtonGroup, Flex, useDisclosure } from '@chakra-ui/react';
import { AlertComponent } from 'components/alerts/AlertComponent';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import Card from 'components/card/Card';
import CheckTable from 'components/dataTables/CheckTable';
import { EditModal } from 'components/modal/EditModal';
import { RecalculateAverageTicketModal } from 'components/modal/RecalculateAverageTicketModal';
import { useGlobalContext } from 'contexts/GlobalContext';
import { SidebarHelpContext } from 'contexts/SidebarHelpContext';
import { ISidebarHelp } from 'models/sidebarHelp/sidebarHelp.model';
import { RowEditIcon } from 'pages/admin/demand/averageTicket/components/RowEdit';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { URL_CT360, URL_FALE_CONOSCO } from 'utils/consts/global';

import { averageTicketMessages } from './variables/averageTicketMessages';
import { columnsDataPrecoMedio } from './variables/columnsDataPrecoMedio';
import { helpContent } from './variables/helpContent';
interface IAverageTicket {
	id: number;
	nome_canal_venda: string;
	nome_familia: string;
	nome_produto: string;
	codigo_produto: string;
	ticket_medio_calculado: number;
	ticket_medio_editado: number;
	recalcular: boolean;
}

export default function CadastroPrecoMedio() {
	const { isOpen, onOpen, onClose } = useDisclosure();

	const history = useHistory();

	const { pathCompanyName } = useGlobalContext();

	const { setters } = useContext(SidebarHelpContext);

	const { setDataSidebar } = setters;

	const tableColumns = useMemo(() => columnsDataPrecoMedio, []);

	const [hasBaseData, setHasBaseData] = useState<boolean>(false);

	const [hasNegativeValue, setHasNegativeValue] = useState(false);

	const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
	const [editId, setEditId] = useState<number | null>(null);

	const helpAverageTicket: ISidebarHelp = useMemo(
		() => ({
			title: averageTicketMessages.SCREEN_TITLES.AVERAGE_TICKET,
			firstParagraph: helpContent.HELP_AVERAGE_TICKET_DEFINITION,
			secondParagraph: helpContent.HELP_EDIT_AVERAGE_TICKET,
			thirdParagraph: helpContent.HELP_AVERAGE_TICKET_CALCULATION_RULE,
			firstLink: URL_CT360,
			secondLink: URL_FALE_CONOSCO,
		}),
		[]
	);

	const RegisterAverageTicket = useCallback(() => {
		history.push(`/${pathCompanyName}/demanda/ticketMedio/criar`);
	}, [history, pathCompanyName]);

	const textHelper = helpContent.HELP_TEXT;

	const renderRow = useCallback((averageTicket: IAverageTicket) => {
		return {
			id: averageTicket.id,
			canalVenda: averageTicket?.nome_canal_venda,
			familia: averageTicket?.nome_familia,
			produto: averageTicket?.nome_produto,
			codProduto: averageTicket?.codigo_produto,
			ticketMedio: averageTicket.recalcular
				? averageTicket?.ticket_medio_calculado?.toLocaleString(
						'pt-BR',
						{
							style: 'currency',
							currency: 'BRL',
						}
				  )
				: averageTicket?.ticket_medio_editado?.toLocaleString('pt-BR', {
						style: 'currency',
						currency: 'BRL',
				  }),
			recalcular: averageTicket.recalcular,
			button: (
				<RowEditIcon
					onClick={() => {
						setEditId(averageTicket.id);
						setEditModalOpen(true);
					}}
				/>
			),
		};
	}, []);

	const renderButtons = useCallback(() => {
		return (
			<Flex
				flexDirection={'row'}
				justifyContent={'flex-end'}
				mt={'20px'}
				me={'20px'}
				mb={'40px'}
			>
				<ButtonGroup spacing={'20px'}>
					<ButtonComponent
						data-test='button-recalcular-page-preco_medio'
						type={'secondary'}
						title={averageTicketMessages.LABELS.RECALCULATE_BUTTON}
						onClick={onOpen}
					/>
					<ButtonComponent
						data-test='button-cadastrar-preco_medio'
						type={'primary'}
						title={
							averageTicketMessages.LABELS
								.REGISTER_AVERAGE_TICKET_BUTTON
						}
						onClick={RegisterAverageTicket}
					/>
				</ButtonGroup>
				<RecalculateAverageTicketModal
					isOpen={isOpen}
					onClose={onClose}
				/>
			</Flex>
		);
	}, [RegisterAverageTicket, isOpen, onClose, onOpen]);

	function renderTable() {
		return (
			<CheckTable
				dataTest='button-filtrar-preco_medio'
				module={'ticketMedio'}
				textHelper={textHelper}
				linkEditForm={null}
				columnsData={tableColumns}
				showGenericSearchInput={false}
				filterModule='ticketMedio'
				renderRow={renderRow}
				setHasBaseData={setHasBaseData}
				setHasNegativeValue={setHasNegativeValue}
				ml='20px'
			/>
		);
	}

	function renderAlerts(
		action: boolean,
		status: 'error' | 'warning',
		description: string,
		hasCloseButton?: boolean
	) {
		return (
			action && (
				<AlertComponent
					title='Atenção!'
					status={status}
					description={description}
					hasCloseButton={hasCloseButton}
				/>
			)
		);
	}

	useEffect(() => {
		setDataSidebar(helpAverageTicket);
	}, [helpAverageTicket, setDataSidebar]);

	return (
		<Flex
			direction='column'
			pt={{ sm: '125px', lg: '75px' }}
		>
			<Card px='0px'>
				{renderAlerts(
					hasNegativeValue,
					'warning',
					averageTicketMessages.MSG_WARNING_NEGATIVE_AVERAGE_TICKET,
					true
				)}
				{renderButtons()}
				{renderTable()}
				{renderAlerts(
					!hasBaseData,
					'error',
					averageTicketMessages.MSG_EDIT_AVERAGE_TICKET_INSTRUCTION
				)}
				{editId !== null && (
					<EditModal
						id={editId}
						checked={false}
						title={
							averageTicketMessages.SCREEN_TITLES
								.EDIT_AVERAGE_TICKET
						}
						text={
							averageTicketMessages.MSG_EDIT_AVERAGE_TICKET_INSTRUCTION
						}
						firstLabel={averageTicketMessages.LABELS.PRODUCT}
						secondLabel={
							averageTicketMessages.LABELS.AVERAGE_TICKET
						}
						isOpen={editModalOpen}
						onClose={() => setEditModalOpen(false)}
						showSwitch={false}
						showRecalculate={false}
					/>
				)}
			</Card>
		</Flex>
	);
}
